/**
 * ThesisPerformanceChart — Plotly chart showing per-thesis-set return curves
 * overlaid with benchmark (SPY). Uses thesis_perf_snapshots API.
 */
import { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import * as api from '../lib/api';

const PERIODS = ['1m', '3m', 'ytd', '1y', 'all'] as const;

const COLORS = ['#6366f1', '#f59e0b', '#22c55e', '#ef4444', '#8b5cf6', '#06b6d4'];

interface Props {
  thesisIds: string[];
  thesisNames: Record<string, string>;
}

export function ThesisPerformanceChart({ thesisIds, thesisNames }: Props) {
  const [period, setPeriod] = useState<string>('all');
  const [data, setData] = useState<Record<string, api.ThesisPerfResponse>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (thesisIds.length === 0) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all(
      thesisIds.map((id) =>
        api.portfolio.thesisPerformance(id, period)
          .then((r) => [id, r] as const)
          .catch(() => [id, null] as const)
      ),
    ).then((results) => {
      const map: Record<string, api.ThesisPerfResponse> = {};
      for (const [id, r] of results) {
        if (r && r.points.length > 0) map[id] = r;
      }
      setData(map);
    }).finally(() => setLoading(false));
  }, [thesisIds.join(','), period]);

  if (loading) return <div className="text-xs text-ink-mute">Loading thesis performance...</div>;
  if (Object.keys(data).length === 0) {
    return (
      <div className="card p-4 text-xs text-ink-mute" data-testid="thesis-performance-empty">
        No thesis performance data yet. Snapshots are recorded daily for open theses.
      </div>
    );
  }

  const traces: Plotly.Data[] = [];
  let idx = 0;
  for (const [id, perf] of Object.entries(data)) {
    traces.push({
      x: perf.points.map((p) => p.date),
      y: perf.points.map((p) => p.return_pct),
      type: 'scatter',
      mode: 'lines',
      name: thesisNames[id] || id,
      line: { color: COLORS[idx % COLORS.length], width: 2 },
    });
    if (perf.points.some((p) => p.benchmark_return_pct !== null) && idx === 0) {
      traces.push({
        x: perf.points.map((p) => p.date),
        // AE-010 anti-hallucination Layer 4: nulls remain undefined so
        // Plotly draws a visible GAP. connectgaps:false hardens this
        // (default would draw a straight line across the missing point).
        y: perf.points.map((p) => p.benchmark_return_pct ?? undefined) as Plotly.Datum[],
        type: 'scatter',
        mode: 'lines',
        name: 'SPY Benchmark',
        line: { color: '#94a3b8', width: 1.5, dash: 'dash' },
        connectgaps: false,
      });
    }
    idx++;
  }

  return (
    <div className="card p-4" data-testid="thesis-performance-chart">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">Thesis Performance</h3>
        <div className="flex gap-1">
          {PERIODS.map((p) => (
            <button
              key={p}
              className={`btn text-xs ${p === period ? 'border-accent text-accent' : ''}`}
              onClick={() => setPeriod(p)}
            >
              {p}
            </button>
          ))}
        </div>
      </div>
      <Plot
        data={traces}
        layout={{
          height: 280,
          margin: { t: 10, b: 40, l: 50, r: 20 },
          xaxis: { showgrid: false },
          yaxis: { title: { text: 'Return (%)' }, showgrid: true, gridcolor: '#e2e8f0', ticksuffix: '%' },
          legend: { orientation: 'h', y: -0.2 },
          paper_bgcolor: 'transparent',
          plot_bgcolor: 'transparent',
          font: { size: 11, color: '#64748b' },
        }}
        config={{ displayModeBar: false, responsive: true }}
        style={{ width: '100%' }}
      />
    </div>
  );
}
